<template>
  <div
    v-if="product && !bundleSizeSelect"
    class="c-product-card l-flex"
    :class="{
      'c-product-card--new': isNew,
    }"
    role="link"
    :labelledby="cardLabelId"
    @mouseover="hover = true"
    @mouseleave="hover = false"
    @click="navigate"
  >
    <div class="c-product-card__container">
      <div
        class="c-product-card__image-container l-flex"
        :class="{
          'c-product-card__image-container--compact': compact
        }"
      >
        <badge
          v-if="badgeText"
          class="c-badge__align-top-left"
          :background-class="badgeBackgroundColor"
        >
          {{ badgeText }}
        </badge>
        <badge
          v-if="isClearanceItem"
          class="c-badge__align-top-left"
          background-class="u-background-color-secondary"
        >
          Clearance
        </badge>
        <div
          class="c-product-card__backdrop"
          :class="{'c-product-card__backdrop--show':hover}"
          :style="imageContainerStyle"
        />
        <card-image
          v-if="featuredImage"
          class="c-product-card__image"
          :class="{
            'c-product-card__image--hide': hover
          }"
          :src="featuredImage.src"
          :alt-text="altText"
          size="300x300"
        />
        <p
          v-if="imageTitle"
          class="c-product-card__image-title t-heading-12 t-heading-12--x-light"
          :aria-label="`${imageTitle}.`"
        >
          {{ imageTitle }}
        </p>
      </div>
      <div class="c-product-card__content-container">
        <star-rating
          v-if="showStars"
          :product="product"
          variant="product-card"
        />
        <p
          v-if="title"
          class="c-product-card__title t-heading-6 u-margin-bottom-xx-small"
          :aria-label="`Product Name ${title}.`"
        >
          {{ title }}
        </p>
        <p
          v-else-if="productTitle"
          class="c-product-card__title t-heading-6 u-margin-bottom-xx-small"
          :aria-label="`${productTitle}.`"
        >
          {{ productTitle }}
        </p>
        <div
          class="l-flex--justify-start l-flex u-margin-bottom-small"
        >
          <p
            v-if="price"
            class="c-product-card__price t-heading-8"
            :aria-label="`price: $${price}.`"
          >
            {{ price | currency }}
          </p>
          <p
            v-if="originalPrice"
            class="t-heading-8 t-heading-8--light u-strikeout"
            :aria-label="`original price: $${originalPrice}.`"
          >
            <span>{{ originalPrice | currency }}</span>
          </p>
        </div>
        <dropdown
          v-if="isMobile && bundle"
          :options="sizeValues"
          placeholder="SELECT"
          class="c-product-card__sizepicker u-margin-bottom-small"
        />
        <btn
          v-if="!isMobile && bundle"
          :id="cardLabelId"
          class="c-product-card__cta-button"
          :class="{
            'c-product-card__cta-button--show': hover
          }"
          :class-modifiers="buttonClassModifiers"
          :aria-label="cardLabel"
          @click="clickSelect()"
          @focus.native="hover = true"
          @blur.native="hover = false"
        >
          SELECT
        </btn>
        <btn
          v-if="!bundle && !disableButton"
          :id="cardLabelId"
          class="c-product-card__cta-button"
          :class="{
            'c-product-card__cta-button--show': hover || showButton
          }"
          :class-modifiers="buttonClassModifiers"
          :url="buttonUrl"
          :aria-label="cardLabel"
          @click="$emit('click', $event)"
          @focus.native="hover = true"
          @blur.native="hover = false"
        >
          Shop Now
        </btn>
      </div>
    </div>
  </div>
  <div
    v-else-if="product && bundle && !isMobile && bundleSizeSelect"
  >
    <slot />
  </div>
</template>

<script>
import Badge from '../badge/Badge'
import Btn from '../button/Btn'
import CardImage from './CardImage'
import get from 'lodash/get'
import { productIsNew } from '@/utils/shopifyHelper'
import Dropdown from '../dropdown/Dropdown.vue'
export default {
  name: 'ProductCard',
  components: {
    Btn,
    Badge,
    CardImage,
    Dropdown,
    StarRating: () => import('../../screens/StarRating')
  },
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    imageSize: {
      type: String,
      default: '250x250',
    },
    enableStars: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: ''
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    bundle: {
      type: Object,
      default: null
    },
    bundleSlotCount: {
      type: Number,
      default: 0
    },
    irregularPrices: {
      type: Boolean,
      default: false,
    },
    showButton: {
      type: Boolean,
      default: false,
    },
    disableButton: {
      type: Boolean,
      default: false,
    },
    badgeColor: {
      type: String,
      default: 'secondary'
    }
  },
  data() {
    return {
      hover: false,
      buttonClassModifiers: ['primary'],
      averageScore: 0,
      totalReviews: 0,
      bundleSizeSelect: false,
    }
  },
  computed: {
    cardLabelId() {
      const productId = this.product && this.product.id
      const id = this._uid
      return `product-card-label-${id}--${productId}`
    },
    cardLabel() {
      const collectionLabel = get(
        this.product,
        'metafieldsMap.collection_label.value'
      )
      const productTitle = this.productTitle
      const price = this.price
      return `${collectionLabel}: ${productTitle}. Price: $${price}`
    },
    altText() {
      let result = ''
      const product = this.product
      const featuredImage = this.featuredImage
      const title = this.title || get(featuredImage, 'alt')
      const type = get(product, 'productType') || ''
      const description = get(product, 'metafieldsMap.color.value.description') || ''
      result = `Image of ${title} ${type} - ${description}.`
      return result
    },
    showStars() {
      const enableStars = this.enableStars
      return !!(enableStars)
    },
    isClearanceItem() {
      const product = this.product
      const tags = get(product, 'tags') || []
      return tags.includes('clearance')
    },
    isNew() {
      const product = this.product
      return productIsNew(product)
    },
    imageContainerStyle() {
      if(this.hoverImage){
        return "backgroundImage: url(" + this.hoverImage.src + ")"
      }
      return "";
    },
    featuredImage () {
      let result = get(this.product, 'featuredImage')
      if (!result && (process.env.NODE_ENV === 'debug')) {
        console.warn('[Product Card]: Invalid or missing featured Image!')
      }
      return result
    },
    hoverImage () {
      let result = get(this.product, 'hoverImage')
      if (!result) {
        result = get(this.product, 'featuredImage')
      }
      if (!result && (process.env.NODE_ENV === 'debug')) {
        console.warn('[Product Card]: Invalid or missing featured media source and hover image!')
      }
      return result
    },
    imageTitle () {
      let result = get(this.product, 'imageTitle')
      if (!result && (process.env.NODE_ENV === 'debug')) {
        console.warn('[Product Card]: Invalid or missing image title!')
      }
      return result
    },
    productTitle () {
      let result = (
        get(this.product, 'title') ||
        get(this.product, 'productTitle')
      )
      return result
    },
    price () {
      const bundle = this.bundle;
      const product = this.product;
      const bundleSlotCount = this.bundleSlotCount;
      let result = get(bundle || product, 'variants[0].price')
      if (!result && (process.env.NODE_ENV === 'debug')) {
        console.warn('[Product Card]: Invalid or missing current price!')
      }
      if (bundle) {
        result = String((+result / bundleSlotCount).toFixed(2))
      }
      if (result == 0.0 && process.env.NODE_ENV !== 'production') {
        console.warn('Bundle is missing price!')
      }
      return result
    },
    originalPrice () {
      let result;
      const irregularPrices = this.irregularPrices;
      const bundle = this.bundle;
      const product = this.product;
      const bundleSlotCount = this.bundleSlotCount;
      if(bundle && irregularPrices) {
        console.warn('[Product Card]: Ring prices irregular - displaying variant compare at price')
        result = get(product, 'variants[0].compareAtPrice')
      } else {
        result = get(bundle || product, 'variants[0].compareAtPrice')
      }
      if (!result && (process.env.NODE_ENV === 'debug')) {
        console.warn('[Product Card]: Invalid or missing original price!')
      }
      if (bundle && !irregularPrices) {
        result = String((+result / bundleSlotCount).toFixed(2))
      }
      return (result === '0.00') ? undefined : result
    },
    buttonUrl () {
      let handle = get(this.product, 'handle')
      return `/products/${handle}`
    },
    backgroundColor () {
      let result = get(this.product, 'backgroundColor')
      if (!result && (process.env.NODE_ENV === 'debug')) {
        console.warn('[Product Card]: Invalid or missing background color!')
      }
      return result
    },
    badgeText () {
      const product = this.product
      const badgeLabel = get(product, 'metafieldsMap.badge_label.value')
      const month = get(product, 'metafieldsMap.month.value')
      const result =  badgeLabel || month
      return result
    },
    badgeBackgroundColor() {
      const product = this.product
      let badgeColor = this.badgeColor
      const color = get(product, 'metafieldsMap.badge_color.value') || badgeColor
      let result = `u-background-color-${color}`
      return result
    }
  },
  methods: {
    navigate(clickable, event) {
      if (this.bundle) {
        this.bundleSizeSelect = true;
        this.$emit('select', this.bundleSizeSelect, this.product);
      } else {
        const buttonUrl = this.buttonUrl
        if (clickable && buttonUrl) {
          this.$router.push(this.buttonUrl)
          this.$emit('click', event)
        }
      }
    },
    clickSelect() {
      this.bundleSizeSelect = true;
      //console.log(this.bundleSizeSelect, this.product)
      this.$emit('select', this.bundleSizeSelect, this.product);
    }
  }
};
</script>

<style lang="scss" scoped>
.c-product-card {
  @include buttonReset;
  text-align: left;
  cursor: pointer;
  width: 100%;

  &--grab-cursor {
    cursor: grab;
  }

  &--new {
    position: relative;
    &::after {
      content: "";
      background-image: url("~@/assets/images/new_badge_blue.png");
      background-repeat: no-repeat;
      background-size: 100%;
      width: rem(47px * 1.6);
      height: rem(20px * 1.6);
      position: absolute;
      right: rem(10px);
      top: rem(10px);
    }
  }

  &:hover {
    .c-product-card__cta-button {
      visibility: visible;
    }
    box-shadow: $box-shadow;
  }

  &__container {
    width: 100%;
  }
  &__image-container {
    position: relative;
    height: rem(375px);
    &--compact {
      height: rem(300px);
    }
    @include respond(tab-port) {
      height: rem(200px);
    }
    width: 100%;
    @include themify($themes) {
      background-color: themed('background', 'xx-light');
    }
  }
  &__backdrop {
    position: absolute;
    top: 0;
    left: 0;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: 0.5s;
    &--show{
      opacity: 1;
    }
  }

  &__image {
    aspect-ratio: 1/1!important;
    object-fit: cover;
    @include respond(phone) {
      aspect-ratio: 1/1!important;
      max-height: 100px;
    }
    @include respond(tab-port) {
      max-width: 160px;
      max-height: 90%;
    }
    margin-bottom: 0;
    padding: 0;
    position: relative;
    opacity: 1;
    transition: 0.5s;
    &--hide{
      opacity: 0;
    }
  }

  &__image-title {
    bottom: 14px;
    left: 14px;
    position: absolute;
  }

  &__content-container {
    padding: 14px;
    width: 100%;
  }

  &__ratings-wrapper {
    width: 100%;
    height: rem(20px);
    transition: opacity 2s ease-in;
    opacity: 1;
    overflow: hidden;
    &--pending {
      opacity: 0.5;
    }
  }

  &__ratings {
    color: $star-yellow;
    font-weight: bold;
    font-size: rem(16px);
  }

  &__total-reviews {
    padding-left: rem(8px);
    @include themify($themes) {
      color: themed('foreground', 'light-text');
    }
  }

  &__price {
    margin-right: rem(5px);
  }
  &__cta-button {
    opacity: 0;
    width: 100%;
    transition: opacity 0.5s;
    &--show{
      opacity: 1;
    }
  }
  ::v-deep {
      .c-dropdown::after {
          content: "";
        }
      .c-dropdown__select {
        font-size: rem(15px);
        font-weight: $font-weight-demi;
        line-height: rem(20px);
        letter-spacing: rem(1px);
      }
    }
  &__sizepicker {
    width: 100%;
    &,
    &:link,
    &:visited {
      @include themify($themes) {
        background-color: themed('foreground', 'white-text');
        color: themed('foreground');
        border: 2px solid themed('foreground');
      }
    }
    &:hover {
      @include themify($themes) {
        color: themed('foreground');
        border:2px solid themed('foreground');
        background-color: themed('foreground', 'white');
      }
    }
    &:active,
    &:focus {
      @include themify($themes) {
        background-color: themed('foreground', 'white');
        color: themed('foreground');
      }
    }
    &--disabled {
      &,
      &:link,
      &:visited {
        cursor: no-drop;
        pointer-events: none;
        @include themify($themes) {
          color: themed('foreground', 'base');
          background-color: themed('background', 'white');
          border: 2px themed('foreground', 'foreground') solid;
        }
      }
    }
  }
}
</style>
